import React, { useMemo, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { Button, Form, ModalBody } from 'react-bootstrap';
import { FadeIn, FloatingField, FadeAlert } from '../../Global/Transitions';
import { SuccessIcon, LoaderIcon } from '../../Global/Icons';
import Tests from '../../../Models/Tests';
import {
  createRepeatAssessment,
  RepeatAssessmentResponse
} from '../../../Services/PtaasApiService';

const invalidProjectNamePattern = /[<>:"/\\|?*]/;

interface RepeatAssessmentProps {
  test: Tests;
  close: () => void;
  complete: () => void;
}

type Step = 0 | 1 | 2;

const RepeatAssessment: React.FC<RepeatAssessmentProps> = ({ test, close, complete }) => {
  const { instance, accounts } = useMsal();
  const todayDateString = useMemo(() => new Date().toISOString().split('T')[0], []);

  const [step, setStep] = useState<Step>(0);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<RepeatAssessmentResponse | null>(null);

  const [noSignificantChangesConfirmed, setNoSignificantChangesConfirmed] = useState(false);
  const [newProjectName, setNewProjectName] = useState(test.projectName);
  const [hasEditedProjectName, setHasEditedProjectName] = useState(false);
  const [requestedStartDate, setRequestedStartDate] = useState('');
  const [requestedEndDate, setRequestedEndDate] = useState('');
  const [comments, setComments] = useState('');

  const isStartDateBeforeToday = requestedStartDate.length > 0 && requestedStartDate < todayDateString;
  const isEndDateBeforeToday = requestedEndDate.length > 0 && requestedEndDate < todayDateString;
  const isEndDateBeforeStartDate =
    requestedEndDate.length > 0 &&
    requestedStartDate.length > 0 &&
    requestedEndDate < requestedStartDate;

  const isInvalidProjectName = useMemo(() => {
    const trimmed = newProjectName.trim();
    return (
      trimmed.length > 0 &&
      (invalidProjectNamePattern.test(trimmed) || trimmed === '.' || trimmed === '..')
    );
  }, [newProjectName]);

  const titleByStep = useMemo(() => {
    switch (step) {
      case 0:
        return 'Repeat Assessment';
      case 1:
        return 'Confirm Eligibility';
      case 2:
        return 'New Project Details';
      default:
        return 'Repeat Assessment';
    }
  }, [step]);

  const canGoNext = useMemo(() => {
    if (step === 0) return true;
    if (step === 1) return noSignificantChangesConfirmed;
    if (step === 2) {
      return (
        newProjectName.trim().length > 0 &&
        requestedStartDate.length > 0 &&
        requestedEndDate.length > 0 &&
        !isStartDateBeforeToday &&
        !isEndDateBeforeToday &&
        !isEndDateBeforeStartDate &&
        newProjectName.trim().toLowerCase() !== test.projectName.trim().toLowerCase() &&
        !isInvalidProjectName
      );
    }

    return false;
  }, [
    step,
    noSignificantChangesConfirmed,
    newProjectName,
    requestedStartDate,
    requestedEndDate,
    test.projectName,
    isInvalidProjectName,
    isStartDateBeforeToday,
    isEndDateBeforeToday,
    isEndDateBeforeStartDate
  ]);

  const next = () => {
    if (step < 2) {
      setError(null);
      setStep((current) => (current + 1) as Step);
    }
  };

  const back = () => {
    if (step > 0) {
      setError(null);
      setStep((current) => (current - 1) as Step);
    }
  };

  const submit = async () => {
    setIsProcessing(true);
    setError(null);

    try {
      const response = await createRepeatAssessment(instance, accounts[0], test.id, {
        noSignificantChangesConfirmed,
        newProjectName: newProjectName.trim(),
        requestedStartDate: new Date(requestedStartDate).toISOString(),
        requestedEndDate: new Date(requestedEndDate).toISOString(),
        comments: comments.trim()
      });

      setResult(response);
    } catch (e) {
      setError((e as Error).message || 'Failed to create repeat assessment.');
    } finally {
      setIsProcessing(false);
    }
  };

  const onContinue = () => {
    complete();
  };

  if (result) {
       return (
      <ModalBody>
        <FadeIn>
          <div className='text-center py-5'>
            <SuccessIcon />
            <h1 className='m-0'>Project Created</h1>
            <p>Congratulations! Your new project has been created successfully.</p>
            <Button variant='primary' onClick={onContinue}>Continue</Button>
          </div>
        </FadeIn>
      </ModalBody>
    );
  }

  return (
    <ModalBody>
      {step > 0 ? <h3 className='mb-3'>{titleByStep}</h3> : null}

      <FadeAlert show={!!error} dismissFunction={() => setError(null)}>{error}</FadeAlert>

      {step === 0 ? (
        <div>
          <p>
            Repeat Assessment creates a new project based on the previous assessment.
          </p>
          <p className='mb-0'>
            Existing scoping and prerequisite data are carried over to the new project.
          </p>
        </div>
      ) : null}

      {step === 1 ? (
        <div>
          <p>
            No Significant Changes means the client environment, scope, and core business context are materially the same as the last assessment.
          </p>
          <Form.Check
            id='no-significant-changes'
            type='checkbox'
            checked={noSignificantChangesConfirmed}
            onChange={(e) => setNoSignificantChangesConfirmed(e.target.checked)}
            label='I confirm there are no significant changes from last time.'
          />
        </div>
      ) : null}

      {step === 2 ? (
        <Form>
          <FloatingField
            main
            label='New Project Name'
            value={newProjectName}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setHasEditedProjectName(true);
              setNewProjectName(e.target.value);
            }}
            feedback={
              isInvalidProjectName
                ? 'Project name contains invalid file name characters.'
                : 'New project name must be different from the current project name.'
            }
            negative={hasEditedProjectName && (
              (newProjectName.trim().length > 0 &&
                newProjectName.trim().toLowerCase() === test.projectName.trim().toLowerCase()) ||
              isInvalidProjectName
            )}
          />

          <FloatingField
            type='date'
            label='Requested Start Date'
            value={requestedStartDate}
            min={todayDateString}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setRequestedStartDate(e.target.value)}
            feedback='Start date must be today or later.'
            negative={isStartDateBeforeToday}
          />

          <FloatingField
            type='date'
            label='Requested End Date'
            value={requestedEndDate}
            min={todayDateString}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setRequestedEndDate(e.target.value)}
            feedback={
              isEndDateBeforeToday
                ? 'End date must be today or later.'
                : 'End date must be on or after start date.'
            }
            negative={isEndDateBeforeToday || isEndDateBeforeStartDate}
          />

          <FloatingField label='Any Other Comments' required={false} className='mb-0'>
            <Form.Control
              as='textarea'
              rows={3}
              value={comments}
              onChange={(e) => setComments(e.target.value)}
              maxLength={2000}
              placeholder=''
            />
          </FloatingField>
        </Form>
      ) : null}

      <div className='d-flex justify-content-between mt-4'>
        <div>
          {step > 0 ? (
            <Button variant='secondary' onClick={back} disabled={isProcessing}>Back</Button>
          ) : null}
        </div>

        {step < 2 ? (
          <Button variant='secondary' onClick={next} disabled={!canGoNext || isProcessing}>
            Next
          </Button>
        ) : (
          <Button variant='secondary' onClick={submit} disabled={isProcessing}>
            {isProcessing ? <><LoaderIcon width={16} className='me-2' />Creating...</> : 'Submit'}
          </Button>
        )}
      </div>
    </ModalBody>
  );
};

export default RepeatAssessment;
